import { Grid } from '@mui/material';
import {
  CompositeFilterDescriptor,
  DataResult,
  DataSourceRequestState,
  toDataSourceRequestString
} from '@progress/kendo-data-query';
import {
  GridColumn as Column,
  GridSortChangeEvent,
  GridToolbar
} from '@progress/kendo-react-grid';
import { sumBy } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionButton,
  DeleteButton,
  PrimaryButton,
  SecondaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { DateTimeCell } from 'src/components/shared/DataGrid/DateCell';
import { DateRangeFilterCell } from 'src/components/shared/DataGrid/DateRangeFilterCell';
import { EditableGrid } from 'src/components/shared/DataGrid/EditableGrid/EditableGrid';
import {
  extractValuesFromMultiSelectFilter,
  getMultiSelectFilterState,
  MultiSelectFilterCell
} from 'src/components/shared/DataGrid/MultiSelectFilterCell';
import { DescriptionList } from 'src/components/shared/DescriptionList/DescriptionList';
import { Header3 } from 'src/components/shared/Typography/Header3';
import { getRequest } from 'src/store/api-handler';
import { GradeDto } from 'src/store/grades/grade';
import { ProductDetailDto, ProductDto } from 'src/store/products/product';
import { mapProductDetailToProduct } from 'src/store/products/product.map';
import { ProductProductDto } from 'src/store/products/productProduct';
import { RunSystemDto } from 'src/store/runsystems/runsystem';

type Props = {
  product: Nullable<ProductDetailDto>;
  grades: GradeDto[];
  runSystems: RunSystemDto[];
  requestState: DataSourceRequestState;
  onFilterChange: (filter: CompositeFilterDescriptor | undefined) => void;
  onResetFilters: () => void;
  onSortChange: (event: GridSortChangeEvent) => void;
  onSaveHasProducts: (productProducts: ProductProductDto[]) => Promise<void>;
  onPageChange: (skip: number, take: number) => void;
};

export const Collections = React.memo((props: Props) => {
  const {
    product,
    grades,
    runSystems,
    requestState,
    onResetFilters,
    onSortChange,
    onFilterChange,
    onSaveHasProducts,
    onPageChange
  } = props;

  let uniqueId = 0;
  const { t } = useTranslation();

  const [currentAmount, setCurrentAmount] = useState<Nullable<number>>(null);
  const [availableAmount, setAvailableAmount] =
    useState<Nullable<number>>(null);
  const [allProducts, setAllProducts] = useState<DataResult>({
    data: [],
    total: 0
  });
  const [inProducts, setInProducts] = useState<ProductProductDto[]>([]);
  const [hasProducts, setHasProducts] = useState<ProductProductDto[]>([]);
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [selectedRunSystems, setSelectedRunSystems] = useState<string[]>([]);

  useEffect(() => {
    setSelectedGrades(
      extractValuesFromMultiSelectFilter(requestState.filter, 'gradeName')
    );
    setSelectedRunSystems(
      extractValuesFromMultiSelectFilter(requestState.filter, 'runSystemName')
    );
  }, [requestState.filter]);

  const onMultiSelectRunSystem = (field: string, value: string[]) => {
    setSelectedRunSystems(value);
    onFilterChange(
      getMultiSelectFilterState(requestState.filter, field, value)
    );
  };

  const onMultiSelectGrade = (field: string, value: string[]) => {
    setSelectedGrades(value);
    onFilterChange(
      getMultiSelectFilterState(requestState.filter, field, value)
    );
  };

  const getFilteredProducts = useCallback(async () => {
    const queryStr = `${toDataSourceRequestString(requestState)}`;
    const { data } = await getRequest<DataResult>(
      `/products/filtered?${queryStr}`
    );
    // Exclude products that are already in hasProducts
    const filteredData = {
      ...data,
      data: data.data.filter(
        product =>
          !hasProducts.some(hasProduct => hasProduct.product2Id === product.id)
      )
    };
    setAllProducts(filteredData);
  }, [requestState, hasProducts]);

  useEffect(() => {
    if (product) {
      setCurrentAmount(
        sumBy(product.hasProducts, (o: ProductProductDto) => {
          return o.amount ?? 0;
        })
      );
      if (product.producedAmount) {
        setAvailableAmount(
          product.producedAmount -
            sumBy(product.inProducts, (o: ProductProductDto) => {
              return o.amount ?? 0;
            })
        );
      }
      setInProducts(product.inProducts);
      setHasProducts(product.hasProducts);
    }
  }, [product]);

  useEffect(() => {
    getFilteredProducts();
  }, [getFilteredProducts, hasProducts]);

  const onAddProduct = (p: ProductDto) => {
    if (product && !hasProducts.some(x => x.product2Id === p.id)) {
      const productProduct: ProductProductDto = {
        id: --uniqueId,
        product1Id: product.id,
        product2Id: p.id,
        amount: 0,
        product1: mapProductDetailToProduct(product),
        product2: p
      };
      setHasProducts([productProduct, ...hasProducts]);
    }
  };

  const onDeleteProduct = (productProduct: ProductProductDto) => {
    setHasProducts(prevProducts =>
      prevProducts.filter(pp => pp.product2Id !== productProduct.product2Id)
    );
  };

  const onSaveProducts = () => {
    onSaveHasProducts(hasProducts);
  };

  const onCancel = () => {
    if (product) setHasProducts(product.hasProducts);
  };

  return (
    <section className="flex">
      <Grid container>
        <Grid item md={12} xs={12} lg={12}>
          <DescriptionList className="mb-4">
            <li>
              {t('props.count')}: {product?.hasProducts.length}
            </li>
            <li>
              {t('product.currentAmount')}: {currentAmount}
            </li>
            <li>
              {t('product.availableAmount')}: {availableAmount}
            </li>
          </DescriptionList>
        </Grid>
        <Grid item xs={12} lg={8}>
          <PrimaryButton
            icon="la-save"
            onClick={onSaveProducts}
            className="mr-24"
          >
            {t('save.verb')} {t('product.plural')}
          </PrimaryButton>
          <TertiaryButton onClick={onCancel}>{t('undo')}</TertiaryButton>
        </Grid>
        <Grid item md={12} xs={12} lg={12}>
          <Header3 className="mb-8">{t('product.inProducts')}</Header3>
          <DataGrid
            items={inProducts}
            style={{ width: '50%' }}
            filterable={false}
          >
            <Column field="product1.runNo" title={t('product.runNo')} />
            <Column
              field="product1.runSystemName"
              title={t('runSystem.singular')}
            />
            <Column field="product1.gradeName" title={t('grade.singular')} />
            <Column field="product1.lotNo" title={t('product.lotNo')} />
            <Column field="amount" title={t('props.amount')} editable={false} />
          </DataGrid>
        </Grid>
        <Grid item md={6} xs={6} lg={6}>
          <Header3 className="mb-8">{t('product.hasProducts')}</Header3>
          <EditableGrid
            items={hasProducts}
            style={{ height: 1000 }}
            onItemsChange={items => {
              setHasProducts(items);
            }}
          >
            <Column
              field="product2.runNo"
              title={t('product.runNo')}
              filterable={false}
            />
            <Column
              field="product2.runSystemName"
              title={t('runSystem.singular')}
              filterable={false}
            />
            <Column
              field="product2.gradeName"
              title={t('grade.singular')}
              filterable={false}
            />
            <Column
              field="product2.lotNo"
              title={t('product.lotNo')}
              filterable={false}
            />
            <Column
              field="amount"
              title={t('props.amount')}
              filterable={false}
              editable={true}
              editor="numeric"
            />
            <Column
              filterable={false}
              editable={false}
              width="80px"
              cell={props => (
                <td style={{ textAlign: 'right' }}>
                  <DeleteButton
                    onClick={() => {
                      onDeleteProduct(props.dataItem);
                    }}
                  />
                </td>
              )}
            />
          </EditableGrid>
        </Grid>
        <Grid item md={6} xs={6} lg={6}>
          <Header3 className="mb-8">{t('product.all')}</Header3>
          <DataGrid
            data={allProducts}
            style={{ height: 1000 }}
            pageable
            filter={requestState.filter}
            sort={requestState.sort}
            skip={requestState.skip}
            take={requestState.take}
            pageSize={20}
            onSortChange={onSortChange}
            onFilterChange={event => {
              onPageChange(0, 20);
              onFilterChange(event.filter);
            }}
            onPageChange={onPageChange}
            onRowClick={({ dataItem }) => onAddProduct(dataItem)}
          >
            <GridToolbar>
              <SecondaryButton
                label="Nullstill filter"
                name="resetFilters"
                className="ml-8"
                onClick={onResetFilters}
                disabled={
                  !requestState.filter ||
                  requestState.filter.filters.length === 0
                }
              />
            </GridToolbar>
            <Column field="runNo" title={t('product.runNo')} filter="numeric" />

            <Column
              field="runSystemName"
              title={t('runSystem.singular')}
              filterCell={props => (
                <MultiSelectFilterCell
                  {...props}
                  value={selectedRunSystems}
                  options={runSystems}
                  optionField="name"
                  onValueChange={value =>
                    onMultiSelectRunSystem('runSystemName', value)
                  }
                />
              )}
            />
            <Column
              field="gradeName"
              title={t('grade.singular')}
              filterCell={props => (
                <MultiSelectFilterCell
                  {...props}
                  value={selectedGrades}
                  options={grades}
                  optionField="name"
                  onValueChange={value =>
                    onMultiSelectGrade('gradeName', value)
                  }
                />
              )}
            />
            <Column field="lotNo" title={t('product.lotNo')} filter="numeric" />
            <Column
              field="runDate"
              title={t('product.runDate')}
              cell={props => <DateTimeCell {...props} />}
              filterCell={props => (
                <DateRangeFilterCell
                  {...props}
                  requestState={requestState}
                  onFilterChange={onFilterChange}
                />
              )}
            />
            <Column
              filterable={false}
              editable={false}
              width="100px"
              cell={props => (
                <td style={{ textAlign: 'right' }}>
                  <ActionButton
                    icon="la la-plus-circle"
                    onClick={() => {
                      onAddProduct(props.dataItem);
                    }}
                  />
                </td>
              )}
            />
          </DataGrid>
        </Grid>
      </Grid>
    </section>
  );
});
